import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getQuotes } from '../services/supplierService';
import { Quote, QuoteStatus } from '../models/Quote';
import { QUOTE_STATUS_COLORS, getQuoteStatusLabel, formatAmount } from '../utils/quoteUtils';

const Requests: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQuotes();
      setQuotes(response.quotes || []);
    } catch (error) {
      message.error(t('loadError'));
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const filteredQuotes =
    activeTab === 'all' ? quotes : quotes.filter((quote) => quote.status === activeTab);

  const columns: ColumnsType<Quote> = [
    {
      title: t('quoteNumber'),
      key: 'quote_number',
      render: (_, quote) => quote.quote_number ?? quote.draft_order?.display_id ?? '-',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={QUOTE_STATUS_COLORS[status]}>{getQuoteStatusLabel(status, t)}</Tag>
      ),
    },
    {
      title: t('customer'),
      key: 'customer',
      render: (_, quote) =>
        quote.customer ? `${quote.customer.first_name} ${quote.customer.last_name}` : '-',
    },
    {
      title: t('total'),
      key: 'total',
      render: (_, quote) => formatAmount(quote.draft_order?.total, quote.draft_order?.currency_code),
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
  ];

  const tabItems = [
    { key: 'all', label: t('all') },
    { key: QuoteStatus.PendingServiceProvider, label: t('statusPendingSP') },
    { key: QuoteStatus.PendingCustomer, label: t('statusPendingCustomer') },
    { key: QuoteStatus.Accepted, label: t('statusAccepted') },
    { key: QuoteStatus.CustomerRejected, label: t('statusCustomerRejected') },
    { key: QuoteStatus.ServiceProviderRejected, label: t('statusSPRejected') },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 16 }}>
        {t('requests')}
      </h1>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      <Table<Quote>
        columns={columns}
        dataSource={filteredQuotes}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: t('noRequests') }}
        onRow={(quote) => ({
          onClick: () => navigate(`/requests/${quote.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default Requests;
